import api from '@/services/api';
import type {
  Invitation,
  CreateInvitationRequest,
  SelfRegisterRequest,
  AcceptInvitationRequest,
  PatientPortal,
  SelfScheduleRequest,
} from '@/types/portal';
import type { LoginResponse } from '@/types/auth';

const INVITATIONS_PATH = '/invitations';
const PORTAL_PATH = '/portal';

/** Ensures time string is in HH:mm:ss format for backend TimeOnly. */
function normalizeTime(time: string): string {
  return time.length === 5 ? `${time}:00` : time;
}

/**
 * Patient Portal API service.
 *
 * Handles invitation management, self-registration,
 * invitation acceptance, patient portal views,
 * and self-service scheduling operations.
 */
export const portalService = {
  // -- Invitation Management (Admin) --------------------------

  /**
   * Creates a new patient invitation.
   */
  async createInvitation(payload: CreateInvitationRequest): Promise<Invitation> {
    const { data } = await api.post<Invitation>(INVITATIONS_PATH, payload);
    return data;
  },

  /**
   * Fetches an invitation by its unique token (public).
   */
  async getInvitationByToken(token: string): Promise<Invitation> {
    const { data } = await api.get<Invitation>(`${PORTAL_PATH}/invitations/${token}`);
    return data;
  },

  /**
   * Fetches an invitation by its ID (admin).
   */
  async getInvitationById(id: string): Promise<Invitation> {
    const { data } = await api.get<Invitation>(`${INVITATIONS_PATH}/${id}`);
    return data;
  },

  /**
   * Lists all invitations, optionally filtered by campaign.
   */
  async getInvitations(params?: {
    campaignId?: string;
    status?: string;
  }): Promise<Invitation[]> {
    const { data } = await api.get<Invitation[]>(INVITATIONS_PATH, { params });
    return data;
  },

  /**
   * Lists invitations for a specific campaign.
   */
  async getInvitationsByCampaign(campaignId: string): Promise<Invitation[]> {
    const { data } = await api.get<Invitation[]>(INVITATIONS_PATH, {
      params: { campaignId },
    });
    return data;
  },

  /**
   * Cancels an active invitation.
   */
  async cancelInvitation(id: string): Promise<void> {
    await api.delete(`${INVITATIONS_PATH}/${id}`);
  },

  // -- Patient Self-Service (Public) --------------------------

  /**
   * Self-registers a new patient (no invitation needed).
   * Returns auth tokens so the patient is immediately logged in.
   */
  async selfRegister(payload: SelfRegisterRequest): Promise<LoginResponse> {
    const { data } = await api.post<LoginResponse>(`${PORTAL_PATH}/register`, payload);
    return data;
  },

  /**
   * Accepts an invitation and creates/links a patient record.
   * Returns auth tokens so the patient is immediately logged in.
   */
  async acceptInvitation(
    token: string,
    payload: AcceptInvitationRequest,
  ): Promise<LoginResponse> {
    const { data } = await api.post<LoginResponse>(
      `${PORTAL_PATH}/invitations/${token}/accept`,
      payload,
    );
    return data;
  },

  /**
   * Fetches the patient portal overview for the authenticated patient.
   * Patient is identified from the JWT token (no patientId param needed).
   */
  async getMyPortal(): Promise<PatientPortal> {
    const { data } = await api.get<PatientPortal>(`${PORTAL_PATH}/my-portal`);
    return data;
  },

  /**
   * Self-schedules an appointment from the patient portal.
   * Normalizes time strings to HH:mm:ss format for backend TimeOnly deserialization.
   */
  async selfSchedule(payload: SelfScheduleRequest): Promise<void> {
    await api.post(`${PORTAL_PATH}/appointments`, {
      ...payload,
      startTime: normalizeTime(payload.startTime),
      endTime: normalizeTime(payload.endTime),
    });
  },

  /**
   * Reschedules a patient's appointment from the portal.
   */
  async selfReschedule(
    appointmentId: string,
    payload: { newDate: string; newStartTime: string; newEndTime: string },
  ): Promise<void> {
    await api.put(`${PORTAL_PATH}/appointments/${appointmentId}/reschedule`, {
      ...payload,
      newStartTime: normalizeTime(payload.newStartTime),
      newEndTime: normalizeTime(payload.newEndTime),
    });
  },

  /**
   * Cancels a patient's appointment from the portal.
   */
  async selfCancel(appointmentId: string, reason?: string): Promise<void> {
    await api.put(`${PORTAL_PATH}/appointments/${appointmentId}/cancel`, {
      reason: reason ?? null,
    });
  },
};
